import { showAlert } from '../components/AlertModal';
import i18n from '../i18n';

const FALLBACK_ERROR_KEY = 'errors.somethingWentWrong';

const ERROR_METADATA_KEYS = new Set([
  'message',
  'language_variable',
  'error_code',
  'http_statuscode',
  'status',
]);

/**
 * Return a usable string translation, or null if missing/empty/non-string.
 * Relies on i18n fallbackLng (+ returnEmptyString: false) for empty locale values.
 */
const getTranslationString = (key, t, options) => {
  if (!key) {
    return null;
  }
  const translation = options !== undefined ? t(key, options) : t(key);
  if (typeof translation !== 'string') {
    return null;
  }
  const trimmed = translation.trim();
  if (!trimmed || trimmed === key) {
    return null;
  }
  return trimmed;
};

/**
 * Collect i18n interpolation values from an API error payload.
 * Extra fields from ErrorHandler::throwException($data) are merged onto the
 * response body (and may also appear under `.data`).
 */
export const getApiErrorInterpolationParams = (error) => {
  const responseData = error?.response?.data;
  if (!responseData || typeof responseData !== 'object') {
    return {};
  }

  const nestedData =
    responseData.data &&
    typeof responseData.data === 'object' &&
    !Array.isArray(responseData.data)
      ? responseData.data
      : {};

  const params = { ...responseData, ...nestedData };

  ERROR_METADATA_KEYS.forEach((key) => {
    delete params[key];
  });
  delete params.data;

  return params;
};

/**
 * Resolve a user-facing message from an API error response.
 * Expects error.response.data.language_variable when available.
 * Interpolation vars (e.g. {{max_nights}}) come from the error payload.
 */
export const getApiErrorMessage = (error, t = (...args) => i18n.t(...args)) => {
  const responseData = error?.response?.data;
  const languageVariable = responseData?.language_variable;
  const interpolationParams = getApiErrorInterpolationParams(error);

  if (languageVariable) {
    const translated = getTranslationString(
      `errors.${languageVariable}`,
      t,
      interpolationParams,
    );
    if (translated) {
      return translated;
    }
  }

  const fallback = getTranslationString(FALLBACK_ERROR_KEY, t);
  if (fallback) {
    return fallback;
  }

  const apiMessage =
    typeof responseData?.message === 'string' ? responseData.message.trim() : '';
  if (apiMessage) {
    return apiMessage;
  }

  return 'Something went wrong, please try again.';
};

/**
 * Show the global AlertModal for an API error response.
 * @param {object} error - axios-style error
 * @param {function} [t] - i18n translate function
 */
export const showApiErrorAlert = (
  error,
  t = (...args) => i18n.t(...args),
) => {
  const title = getTranslationString('errors.error', t) || 'Error';
  showAlert(title, getApiErrorMessage(error, t));
};
